/**
 * Copyright 2024-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { Else, If, Then, When } from "react-if";
import { Link } from "react-router-dom";
import { Spacer, Text, theme } from "@nordcloud/gnui";
import { ExecutionPolicy } from "~/generated/graphql";
import { ROUTES } from "~/routing/routes";
import { convertSkipWindow } from "~/utils/convertSkipWindow";
import { DisplayExecutionPolicy } from "~/views/events/components/DisplayExecutionPolicy";
import { DisplayBooleanProperties } from "~/views/events/components/DisplayResourceOperation";
import { DisplayWindowDuration } from "~/views/events/components/DisplayWindowDuration";
import { convertMinutesToTime } from "~/views/events/helpers";
import { EventEntity } from "~/views/events/types";
import { SystemActionNames } from "~/views/plans/PlanCreate/components/PlanCreateWizard/constants";

export function GeneralActionSettings({
  eventEntity,
  planId,
  isBatchEntity,
}: {
  eventEntity?: EventEntity;
  planId?: string;
  isBatchEntity: boolean;
}) {
  const isDelay = eventEntity?.action?.name === SystemActionNames.DELAY;

  return (
    <>
      <Text weight="medium">
        General {isBatchEntity ? "Batch" : "Action"} Settings
      </Text>
      <DisplayExecutionPolicy
        policy={eventEntity?.executionPolicy || ExecutionPolicy.Anyway}
        approvalMode={eventEntity?.approvalMode}
      />
      <If condition={isDelay && eventEntity?.windowDuration}>
        <Then>
          <Text size="sm" tag="div">
            Action Delay
          </Text>
          <Text mb={theme.spacing.spacing01}>
            {convertMinutesToTime(Number(eventEntity?.windowDuration))}
          </Text>
          <Spacer height={theme.spacing.spacing03} />
        </Then>
        <Else>
          <When condition={eventEntity?.windowDuration}>
            <DisplayWindowDuration
              windowDuration={eventEntity?.windowDuration}
            />
          </When>
        </Else>
      </If>

      <When condition={eventEntity?.runInSequence}>
        <DisplayBooleanProperties
          title="Resource Operation"
          condition={eventEntity?.runInSequence}
          options={["Sequenced", "Simultaneous"]}
        />
      </When>
      {eventEntity && "skipWindow" in eventEntity && (
        <>
          <Text size="sm" tag="div">
            Ignore Window
          </Text>
          <Text mb={theme.spacing.spacing01}>
            {convertSkipWindow(eventEntity?.skipWindow)}
          </Text>
          <Spacer height={theme.spacing.spacing04} />
        </>
      )}
      <Link to={`${ROUTES.plans.index}/${planId}`}>
        Go to plan to edit settings
      </Link>
    </>
  );
}
